import { useState } from 'react';
import { Formik, Form } from 'formik';

import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Input from '@src/components/_shared/UI/Input';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import BackButton from '@src/components/_shared/BackButton';
import CodeVerificationModal from '@src/components/_shared/CodeVerificationModal';
import { EmailSchema, PasswordFormSchema } from '@src/utilities/schemas';
import { useMutation } from '@tanstack/react-query';
import Container from '@src/components/_shared/Container';
import toast from 'react-hot-toast';
import { sendVerificationCodeToEmail } from '@src/api/Registration';
import AuthModal from '@src/components/_shared/AuthModal';
import { useLoginMutation } from '@src/hooks/useLoginMutation';
import { handleApiError, isDevOrRe7 } from '@src/utilities/functions';

export default function EmailCheck() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();

    const [showCodeModal, setShowCodeModal] = useState<boolean>(false);
    const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);
    const [hidePasswords, setHidePasswords] = useState<boolean>(true);
    const [showAuthModal, setShowAuthModal] = useState<boolean>(false);

    const _onGoBack = () => {
        if (registration.partner_informations.is_partner) {
            setBlock('Partner')
        } else {
            setSection(0)
            setBlock('BeforeStart')
        }
    };

    const { mutate, isPending } = useMutation({
        mutationKey: ['verifyEmail'],
        mutationFn: (login: string) => sendVerificationCodeToEmail(login, registration.partner_informations.is_partner),
        onSuccess: () => {
            if (registration.partner_informations.is_partner) {
                setBlock('Register')
            } else {
                setShowCodeModal(true)
            }
        },
        onError: (error: any) => {
            if (error.response?.status === 409) {
                toast.error('Cette adresse email ne peut pas être utilisée. Veuillez en saisir une autre.', { position: 'top-center' });
            } else {
                handleApiError(error)
            }
        }
    })

    const closeModal = () => {
        setShowAuthModal(false);
    };

    const { login, isLoginPending, loading } = useLoginMutation(closeModal);
    const handleLogin = (credentials: { login: string; password: string }) => {
        login(credentials);
    };

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Vous avez choisi d’ouvrir un compte titres société, commençons ! " />
            <div style={{ height: '10px' }}/>
            {!showPasswordInput ?
                <Formik
                    initialValues={{ email: registration.person.basic.email }}
                    onSubmit={values => {
                        setField('person.basic.email', values.email);

                        if (!isDevOrRe7()) {
                            mutate(values.email)
                        } else {
                            if (registration.partner_informations.is_partner) {
                                setBlock('Register')
                            } else {
                                setShowCodeModal(true)
                            }
                        }
                    }}
                    validationSchema={EmailSchema}
                >
                    {({ handleChange, handleSubmit, values, errors, isValid, touched, handleBlur }) => {
                        return(
                            <Form id="emailForm" autoComplete="off" className="registerForm">
                                <Input
                                    title="Saisissez l’adresse email du représentant légal"
                                    value={values.email}
                                    onChange={handleChange('email')}
                                    placeholder="Email"
                                    id="email"
                                    name="email"
                                    type="email"
                                    width="100%"
                                    clearInput={() => handleChange('email')('')}
                                    hasError={(touched.email && errors.email) as boolean}
                                    errorMessage={errors.email}
                                    onBlur={handleBlur('email')}
                                />
                                <ValidateButton title="Continuer" onClick={handleSubmit} disabled={!isValid} onLoading={isPending} />
                            </Form>
                        )
                    }}
                </Formik>
                :
                <Formik
                    initialValues={{
                        email: registration.person.basic.email || '',
                        password: registration.person.basic.password || '',
                        confirmPassword: '' }}
                    onSubmit={values => {
                        setField('person.basic.email', values.email);
                        setField('person.basic.password', values.password);
                        if (values.password === values.confirmPassword) {
                            setBlock('Register')
                        }
                    }}
                    validationSchema={PasswordFormSchema}
                >
                    {({ handleChange, handleSubmit, values, errors, isValid, touched, handleBlur }) => {
                        return(
                            <Form id="passwordForm" autoComplete="off" className="registerForm">
                                <Input
                                    title="Saisissez l’adresse email du représentant légal"
                                    value={values.email}
                                    onChange={handleChange('email')}
                                    placeholder="Email"
                                    id="email"
                                    name="email"
                                    type="email"
                                    width="100%"
                                    clearInput={() => handleChange('email')('')}
                                    hasError={(touched.email && errors.email) as boolean}
                                    errorMessage={errors.email}
                                    onBlur={handleBlur('email')}
                                    disabled
                                    showCheckIcon
                                />
                                <Input
                                    title="Saisissez un mot de passe"
                                    value={values.password}
                                    onChange={handleChange('password')}
                                    placeholder="Mot de passe"
                                    id="password"
                                    name="password"
                                    type={hidePasswords ? "password" : "text"}
                                    width="100%"
                                    isPassword
                                    hidePassword={hidePasswords}
                                    setHidePassword={(value: boolean) => setHidePasswords(value)}
                                    clearInput={() => handleChange('password')('')}
                                    hasError={(touched.password && errors.password) as boolean}
                                    errorMessage={errors.password}
                                    onBlur={handleBlur('password')}
                                />
                                <Input
                                    title="Confirmer le mot de passe"
                                    value={values.confirmPassword}
                                    onChange={handleChange('confirmPassword')}
                                    placeholder="Mot de passe"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={hidePasswords ? "password" : "text"}
                                    width="100%"
                                    isPassword
                                    hidePassword={hidePasswords}
                                    setHidePassword={(value: boolean) => setHidePasswords(value)}
                                    clearInput={() => handleChange('confirmPassword')('')}
                                    hasError={(touched.confirmPassword && errors.confirmPassword) as boolean}
                                    errorMessage={errors.confirmPassword}
                                    onBlur={handleBlur('confirmPassword')}
                                />
                                <ValidateButton title="Continuer" onClick={handleSubmit} disabled={!isValid} onLoading={isPending} />
                            </Form>
                        )
                    }}
                </Formik>
            }
            {showCodeModal ?
                <CodeVerificationModal
                    show={showCodeModal}
                    onClose={() => setShowCodeModal(false)}
                    onValidate={() => setShowPasswordInput(true)}
                    retrySendCode={() => mutate(registration.person.basic.email)}
                    showAuthModal={() => setShowAuthModal(true)}
                /> : null}
            {showAuthModal ?
                <AuthModal
                    show={showAuthModal}
                    onValidate={() => setShowAuthModal(false)}
                    onLogin={({login, password}) => handleLogin({login, password})}
                    isLoading={isLoginPending || loading}
                /> : null}
        </Container>
    );
}
